import React, { useState } from 'react';
import { Spinner, Text } from '@fluentui/react-components';
import {
  ArrowSync20Regular,
  Lightbulb20Regular,
  Wrench20Regular,
  Chat20Regular,
  DocumentBulletList20Regular,
  Circle20Regular,
  ErrorCircle20Filled,
  ChevronDown20Regular,
  ChevronRight20Regular,
  CheckmarkCircle20Regular,
} from '@fluentui/react-icons';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import type { ApiReasoningStep } from '../../types/api';

interface ReasoningPanelProps {
  steps: ApiReasoningStep[];
}

export const ReasoningPanel: React.FC<ReasoningPanelProps> = ({ steps }) => {
  const { tokens, modeTokens } = useFluentTokens();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!steps || steps.length === 0) {
    return null;
  }

  const completedCount = steps.filter((s) => s.status === 'completed').length;

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderTop: `1px solid ${modeTokens.colorNeutralStroke2}`,
        paddingTop: tokens.spacingVerticalS,
      }}
    >
      {/* Header toggle */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`Reasoning (${completedCount}/${steps.length} completed)`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacingHorizontalXS,
          cursor: 'pointer',
          padding: `${tokens.spacingVerticalXS} 0`,
        }}
      >
        {isExpanded ? (
          <ChevronDown20Regular style={{ color: modeTokens.colorNeutralForeground2 }} />
        ) : (
          <ChevronRight20Regular style={{ color: modeTokens.colorNeutralForeground2 }} />
        )}
        <Text
          size={300}
          weight="semibold"
          style={{ color: modeTokens.colorNeutralForeground1 }}
        >
          Reasoning
        </Text>
        {completedCount < steps.length && (
          <Text
            size={200}
            style={{ color: modeTokens.colorNeutralForeground3 }}
          >
            ({completedCount}/{steps.length})
          </Text>
        )}
      </Box>

      {/* Steps list */}
      {isExpanded && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: tokens.spacingVerticalXS,
            paddingLeft: tokens.spacingHorizontalS,
          }}
        >
          {steps.map((step) => (
            <ReasoningStepItem key={step.id} step={step} />
          ))}
          {steps.every((s) => s.status === 'completed') && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacingHorizontalS,
                padding: `${tokens.spacingVerticalS} 0`,
                borderLeft: `2px solid ${modeTokens.colorNeutralStroke2}`,
                paddingLeft: tokens.spacingHorizontalM,
                marginLeft: tokens.spacingHorizontalXS,
              }}
            >
              <CheckmarkCircle20Regular style={{ color: modeTokens.colorNeutralForeground2, flexShrink: 0 }} />
              <Text size={300} weight="regular" style={{ color: modeTokens.colorNeutralForeground2 }}>
                Done
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

interface ReasoningStepItemProps {
  step: ApiReasoningStep;
}

const ReasoningStepItem: React.FC<ReasoningStepItemProps> = ({ step }) => {
  const { tokens, modeTokens } = useFluentTokens();

  const getStepTextColor = (status: ApiReasoningStep['status']) => {
    if (status === 'completed') return modeTokens.colorNeutralForeground1;
    if (status === 'error') return modeTokens.colorRedForeground;
    return modeTokens.colorNeutralForeground2;
  };

  const getStepTypeIcon = (id: string, color: string) => {
    const style = { color, flexShrink: 0 as const };
    switch (id) {
      case 'nav-routing':    return <ArrowSync20Regular style={style} />;
      case 'planning':       return <Lightbulb20Regular style={style} />;
      case 'tool_selection': return <Wrench20Regular style={style} />;
      case 'responding':     return <Chat20Regular style={style} />;
      default:               return <DocumentBulletList20Regular style={style} />;
    }
  };

  const getStatusIcon = () => {
    if (step.status === 'in_progress') {
      return <Spinner size="tiny" style={{ width: '20px', height: '20px' }} />;
    }
    if (step.status === 'error') {
      return <ErrorCircle20Filled style={{ color: modeTokens.colorRedForeground }} />;
    }
    if (step.status === 'pending') {
      return <Circle20Regular style={{ color: modeTokens.colorNeutralForeground4 }} />;
    }
    // completed — show the type-specific icon in neutral foreground colour
    return getStepTypeIcon(step.id, modeTokens.colorNeutralForeground2);
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: `${tokens.spacingVerticalS} 0`,
        borderLeft: `2px solid ${modeTokens.colorNeutralStroke2}`,
        paddingLeft: tokens.spacingHorizontalM,
        marginLeft: tokens.spacingHorizontalXS,
      }}
    >
      {/* Step header */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacingHorizontalS,
        }}
      >
        {getStatusIcon()}
        <Text
          size={300}
          weight="regular"
          style={{
            color: getStepTextColor(step.status),
          }}
        >
          {step.label}
        </Text>
      </Box>

      {/* Step details */}
      {step.details && step.details.length > 0 && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.spacingVerticalXXS,
            marginTop: tokens.spacingVerticalXS,
            marginLeft: '28px',
          }}
        >
          {step.details.map((detail) => (
            <Text
              key={detail}
              size={200}
              style={{ color: modeTokens.colorNeutralForeground3 }}
            >
              {detail}
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
};
